import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/node';
import { Link, Outlet, useLoaderData, useLocation } from '@remix-run/react';
import { ChevronRight, Menu, Search } from 'lucide-react';
import { useState } from 'react';
import { HelpEmbed } from '~/components/help-embed';
import { Logo } from '~/components/logo';
import { Boundary } from '~/components/ui/error';
import { Sheet, SheetContent, SheetTrigger } from '~/components/ui/sheet';
import { cn } from '~/utils/cn';
import type { TableOfContents, TableOfContentsSection } from '~/utils/docs';
import { loadContent, tableOfContents } from '~/utils/docs';

export const ErrorBoundary = Boundary;

export async function loader({ request, response }: LoaderFunctionArgs) {
  const content = await loadContent();

  return {
    tableOfContents: tableOfContents(content),
    widgetUrl: process.env.WIDGET_URL!
  };
}

export const links: LinksFunction = () => {
  return [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@200;400;700&display=swap'
    }
  ];
};

export default function ManagerLayout() {
  const data = useLoaderData<typeof loader>();

  return (
    <>
      <div className="text-foreground">
        <Header tableOfContents={data.tableOfContents} />
        <div className="relative flex justify-between">
          <Sidebar tableOfContents={data.tableOfContents} />
          <main className="flex-1 pt-32 lg:pt-14">
            <Outlet />
          </main>
        </div>
      </div>
      <HelpEmbed openStyle="modal" widgetUrl={data.widgetUrl} />
    </>
  );
}

const Sidebar = ({ tableOfContents }: { tableOfContents: TableOfContents }) => {
  return (
    <div className="fixed bottom-0 right-auto top-[4rem] hidden w-64 border-r border-gray-300 lg:block">
      <aside className="absolute inset-0 z-10 overflow-auto pb-10">
        <SharedSidebar tableOfContents={tableOfContents} />
      </aside>
    </div>
  );
};

function MobileSidebar({ tableOfContents }: { tableOfContents: TableOfContents }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Menu className="cursor-pointer" />
        </SheetTrigger>
        <SheetContent side="left" className="!px-0">
          <div className="space-y-4 py-4">
            <div className="px-3 py-2">
              <div className="space-y-1">
                <SharedSidebar setOpen={setOpen} tableOfContents={tableOfContents} />
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

const SharedSidebar = ({
  tableOfContents,
  setOpen
}: {
  tableOfContents: TableOfContents;
  setOpen?: (open: boolean) => void;
}) => {
  const { pathname } = useLocation();

  const renderNavItem = (section: TableOfContentsSection, isCurrent: boolean) => {
    if (isCurrent) {
      const isSame = pathname.includes(section.slug);
      if (isSame) {
        return (
          <li key={section.id}>
            <div className="flex items-center space-x-3 bg-primary py-1.5 pl-4 pr-3 text-white">
              <div>{section.heading}</div>
            </div>

            {section.subsections.length > 0 && (
              <SubSections setOpen={setOpen} section={section} />
            )}
          </li>
        );
      } else {
        return (
          <li key={section.id}>
            <Link
              className="group mt-2 flex cursor-pointer items-center space-x-3 py-1.5 pr-3 text-gray-800 hover:bg-gray-600/5 hover:text-gray-900 focus:outline-primary lg:mt-0"
              style={{ paddingLeft: '1rem' }}
              onClick={() => {
                if (setOpen) setOpen(false);
              }}
              to={`/docs/${section.slug}`}>
              <div>
                <div className="flex flex-1 items-center space-x-2.5">
                  <div>{section.heading}</div>
                </div>
              </div>
            </Link>

            {section.subsections.length > 0 && (
              <SubSections setOpen={setOpen} section={section} />
            )}
          </li>
        );
      }
    }
    return (
      <li key={section.id}>
        <Link
          className={cn(
            'group mt-2 flex cursor-pointer items-center space-x-3 py-1.5 pr-3 text-gray-700 focus:outline-primary lg:mt-0',
            isCurrent
              ? 'bg-primary text-white'
              : 'hover:bg-gray-600/5 hover:text-gray-900'
          )}
          style={{ paddingLeft: '1rem' }}
          onClick={() => {
            if (setOpen) setOpen(false);
          }}
          to={`/docs/${section.slug}`}>
          <div>
            <div
              className={cn(
                'flex flex-1 items-center space-x-2.5',
                !isCurrent && 'grayscale hover:grayscale-0'
              )}>
              <div>{section.heading}</div>
            </div>
          </div>
        </Link>
      </li>
    );
  };

  return (
    <div className="relative lg:leading-6">
      <ul>
        {tableOfContents.map((heading) => {
          const isCurrent = heading.sections.some((s) => pathname.includes(s.slug));

          return (
            <div key={heading.id} className="mt-12 lg:mt-8">
              <h5 className="mb-3.5 pl-4 font-semibold text-gray-900 lg:mb-2.5">
                {heading.heading}
              </h5>
              {heading.sections.map((section) => renderNavItem(section, isCurrent))}
            </div>
          );
        })}
      </ul>
    </div>
  );
};

const SubSections = ({
  section,
  setOpen
}: {
  section: TableOfContentsSection;
  setOpen?: (open: boolean) => void;
}) => {
  // watch for URL changes, read the #hash, to see if the subsection is active
  const { hash } = useLocation();

  return (
    <ul className="mt-1 space-y-1">
      {section.subsections.map((subsection) => (
        <li key={subsection.id}>
          <Link
            onClick={() => {
              if (setOpen) setOpen(false);
            }}
            className={cn(
              'flex items-center space-x-2.5 py-1.5 pl-8 pr-3 text-sm text-gray-700 focus:outline-primary',
              hash.includes(subsection.id)
                ? 'bg-gray-600/5 text-primary'
                : 'hover:bg-gray-600/5 hover:text-gray-900'
            )}
            to={`/docs/${section.slug}#${subsection.id}`}>
            {subsection.heading}
          </Link>
        </li>
      ))}
    </ul>
  );
};

const Header = ({ tableOfContents }: { tableOfContents: TableOfContents }) => {
  const { pathname } = useLocation();
  const currentHeading = tableOfContents.find((section) =>
    section.sections.some((s) => pathname.includes(s.slug))
  );
  const currentSection = currentHeading?.sections.find((s) => pathname.includes(s.slug));

  return (
    <div className="fixed top-0 z-30 w-full">
      <div className="max-w-8xl mx-auto">
        <div className="supports-backdrop-blur:bg-background-light/60 flex-none backdrop-blur transition-colors duration-500">
          <div className="relative lg:border-b lg:border-gray-500/5">
            <div className="flex h-16 items-center border-b border-gray-500/10 bg-teal-200/50 px-4 lg:border-0">
              <div className="relative flex h-full flex-1 items-center gap-x-4">
                <div className="flex flex-1 items-center gap-x-4">
                  <Link to="/">
                    <Logo className="w-20" />
                  </Link>
                </div>
                <div className="pointer-events-auto relative mx-px flex-1 rounded-lg bg-white">
                  <button
                    type="button"
                    onClick={() => window.openAtlasEmbed()}
                    className="bg-background-light flex w-full items-center rounded-lg py-1.5 pl-2.5 pr-3 text-sm leading-6 text-gray-400 shadow-sm ring-1 ring-gray-400/20 hover:ring-gray-600/25 focus:outline-primary dark:text-white/50 dark:ring-1 dark:ring-gray-600/30 dark:brightness-[1.1] dark:hover:ring-gray-500/30 dark:hover:brightness-[1.25]">
                    <Search className="ml-1 mr-3 flex-none text-gray-700 hover:text-gray-800" />
                    Search or ask a question&hellip;
                  </button>
                </div>
                <div className="relative ml-auto hidden flex-1 items-center justify-end lg:flex"></div>
              </div>
            </div>
            <div className="flex h-14 items-center border-b border-gray-500/10 px-5 py-4 lg:hidden">
              <MobileSidebar tableOfContents={tableOfContents} />

              <div className="ml-4 flex min-w-0 space-x-3 whitespace-nowrap text-sm leading-6">
                <div className="flex items-center space-x-3">
                  <span>{currentHeading?.heading}</span>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                </div>
                <div className="truncate font-semibold text-gray-900">
                  {currentSection?.heading}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
